import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import { Router } from 'next/router';
import React, { FC, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { buildProfileMenus, buildQuickAccessMenu } from '@auth/business/auth.utils';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Text } from '@components/Text/Text.component';
import { useLockBodyScroll } from '@layout/business/LockBodyScroll.hook';
import { Navigation } from '@layout/components/Navigation/Navigation.component';
import { PlcDispatch, RootState } from '@store/store';
import { usePlcRouter } from '@utils/customHooks';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import styles from './Menu.module.scss';

export interface MenuProps {
  isPro: boolean;
  isAuthenticated: boolean;
  onSignOut: () => void;
  onSignIn: () => void;
}
const Menu: FC<MenuProps> = ({ isPro, isAuthenticated, onSignOut, onSignIn }) => {
  const isOpened = useSelector((state: RootState) => state.layout.isMenuOpened);
  const menuRef = useRef<HTMLDivElement>(null);
  useLockBodyScroll(isOpened, menuRef.current);
  const {
    layout: { setMenuOpened },
  } = useDispatch<PlcDispatch>();
  const classes = classnames(styles.menu, {
    opened: isOpened,
  });
  const { route } = usePlcRouter();

  useEffect(() => {
    const handleRouteChange = () => {
      setMenuOpened(false);
    };

    Router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      Router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [setMenuOpened]);

  return (
    <div ref={menuRef} className={classes} id="menu" role="menu">
      <div className="flex column flex-1">
        <Navigation className="column flex-1" isMobile>
          <div className={styles.menuSeparator}>
            {isAuthenticated ? (
              <div className={`flex column ${styles.dense}`}>
                {buildProfileMenus(route, 'small')}
                <BasicLink
                  onClick={() => {
                    setMenuOpened(false);
                    onSignOut();
                  }}
                  role="menuitem"
                  textProps={{ i18nKey: 'auth.disconnect', variant: 'small' }}
                />
              </div>
            ) : (
              <PrimaryButton
                isBleuTurquoise
                onClick={() => {
                  // Wordaround: locks of the material ui modal and body scroll are in conflict
                  // other solutions break the modal scroll on iOS
                  clearAllBodyScrollLocks();
                  setTimeout(() => {
                    setMenuOpened(false);
                    onSignIn();
                  });
                }}
                role="menuitem"
                textProps={{ i18nKey: 'auth.connect', variant: 'small' }}
              />
            )}
          </div>
        </Navigation>
        {!isPro && (
          <div className={styles.menuProsLink}>
            <BasicLink
              href={urlEnum.landingPro}
              role="menuitem"
              textProps={{
                flavour: 'yellow',
                i18nKey: 'common.abonner',
                variant: 'h5',
                withArrow: true,
              }}
            />
            <Text flavour="light" i18nKey="common.pro.description" role="menuitem" variant="small" />
          </div>
        )}
        <div className={`flex column plc-p-m ${styles.dense}`}>{buildQuickAccessMenu(route, 'small')}</div>
      </div>
    </div>
  );
};

export { Menu };
